import { useTranslation } from 'react-i18next'
import type { StaffSection } from '@garage/shared'
import { useAuth } from './AuthGate.js'

interface SectionGateProps {
  section?: StaffSection
  anySection?: StaffSection[]
  children: React.ReactNode
}

/**
 * Wraps pages that require a specific section access. If the account is admin
 * or has the required section(s), renders children. Otherwise shows a friendly
 * "no access" message.
 *
 * - `section`: single required section (backward compatible)
 * - `anySection`: account must have at least one of these sections
 * - If neither is specified, only an admin gets in (fails closed)
 */
export function SectionGate({ section, anySection, children }: SectionGateProps) {
  const { t } = useTranslation()
  const { account } = useAuth()

  const required = anySection ?? (section ? [section] : [])
  const hasAccess = account.role === 'admin' || required.some(s => account.sections.includes(s))

  if (hasAccess) {
    return <>{children}</>
  }

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <div className="text-center max-w-md">
        <h1 className="text-display-md text-on-surface mb-2">
          {t('auth.noAccessTitle')}
        </h1>
        <p className="text-body-lg text-on-surface-variant mb-6">
          {t('auth.noAccessBody')}
        </p>
        <a href="/" className="text-primary hover:underline font-semibold">
          Go to dashboard
        </a>
      </div>
    </div>
  )
}
